import { useState } from 'react'
import ReactModal from 'react-modal'
import { Capacitor } from '@capacitor/core'
import { useDispatch, useSelector } from 'react-redux'
import { point, featureCollection } from '@turf/turf'

import { closeModal } from 'store/actions/modals'
import { useReverseGeocode } from 'components/Map'
import { Map } from './MapModalMap'

import { Alert, Button } from 'ui'
import './MapModal.scss'

import { t } from 'translations'
import { isInGeofence } from 'common'

type Coordinates = {
  latitude: number
  longitude: number
}

type Location = {
  latitude: number
  longitude: number
  radius: number
}

interface MapModalProps {
  arrivalCoordinates: Coordinates
  leaveCoordinates: Coordinates
  selected?: 'arrival' | 'departure'
  expectedLocation: Location
  tags?: string[]
}

const MapModal: React.FC<MapModalProps> = ({ arrivalCoordinates, leaveCoordinates, expectedLocation, selected: initial = 'arrival', tags }) => {
  const { tags: allTags } = useSelector((state: MStore.RootState) => state.resources)
  const locations = allTags.filter(tag => tag.type === 'locations').sort((a, b) => (a.name < b.name ? -1 : 1))
  const locationMatch = locations?.find(location => tags?.includes(location._id))
  const address = locationMatch?.address
  const expectedAddress = `${address?.street_number} ${address?.route}, ${address?.locality} ${address?.administrative_area_level_1}, ${address?.postal_code}`

  const dispatch = useDispatch()

  const arrival = arrivalCoordinates ? point([arrivalCoordinates.longitude, arrivalCoordinates.latitude], { name: t(`manager.punch.ARRIVAL`) }) : null
  const departure = leaveCoordinates ? point([leaveCoordinates.longitude, leaveCoordinates.latitude], { name: t(`manager.punch.DEPARTURE`) }) : null
  const expectedPoint = expectedLocation
    ? point([expectedLocation.longitude, expectedLocation.latitude], { name: t(`manager.punch.EXPECTED_LOCATION`), color: 'gray' })
    : null
  const features = [arrival, departure, expectedPoint].filter((val): val is GeoJSON.Feature<GeoJSON.Point, { name: string }> => val !== null)
  const collection = featureCollection(features)

  const [selected, setSelected] = useState(initial)

  const initialSelectedPosition = () => {
    if (selected === 'arrival' && arrival) return arrival.geometry.coordinates
    if (selected === 'departure' && departure) return departure.geometry.coordinates
    return null
  }

  const initialSelectedCoordinates = () => {
    if (selected === 'arrival' && arrival) {
      const [longitude, latitude] = arrival.geometry.coordinates
      return { latitude, longitude }
    }
    if (selected === 'departure' && departure) {
      const [longitude, latitude] = departure.geometry.coordinates
      return { latitude, longitude }
    }
    return null
  }

  const [selectedCoordinates, setSelectedCoordinates] = useState<GeoJSON.Position | null>(initialSelectedPosition())
  const { data: punchAddress } = useReverseGeocode(selectedCoordinates)
  const isSelectedinGeofence = selectedCoordinates
    ? isInGeofence(expectedLocation, { longitude: selectedCoordinates[0], latitude: selectedCoordinates[1] })
    : true

  const selectedError = selectedCoordinates ? !isSelectedinGeofence : false

  const handleSelect = (location: 'arrival' | 'departure') => {
    setSelected(location)
    if (location === 'arrival' && arrival) setSelectedCoordinates(arrival.geometry.coordinates)
    if (location === 'departure' && departure) setSelectedCoordinates(departure.geometry.coordinates)
  }
  return (
    <ReactModal
      isOpen
      overlayClassName={`map-modal-overlay${Capacitor.isNativePlatform() ? ' is-mobile-map' : ''}`}
      shouldFocusAfterRender
      onRequestClose={() => dispatch(closeModal())}
    >
      <div className="d-flex flex-column" style={{ height: '100%', width: '100%' }}>
        <div className="d-flex flex-column">
          <div style={{ height: '100%', width: '100%' }}>
            <div className="d-flex flex-row justify-content-between  border-bottom">
              <div>
                {arrival ? (
                  <Button color="primary" outlined={selected !== 'arrival'} className={`rounded-0 mr-2`} onClick={() => handleSelect('arrival')}>
                    {t(`manager.punch.ARRIVAL`)}
                  </Button>
                ) : null}
                {departure ? (
                  <Button color="primary" outlined={selected !== 'departure'} className={`rounded-0`} onClick={() => handleSelect('departure')}>
                    {t(`manager.punch.DEPARTURE`)}
                  </Button>
                ) : null}
              </div>
              <Button className="close pb-2" aria-label="Close" onClick={() => dispatch(closeModal())}>
                <span aria-hidden="true">&times;</span>
              </Button>
            </div>
          </div>
          {selectedError ? (
            <Alert className="mt-3 border-danger">
              {t(selected === 'arrival' ? 'manager.punch.ARRIVAL_COORDINATES' : 'manager.punch.LEAVE_COORDINATES')}:
              <br />
              <div className="ml-4">
                <small>{t(selected === 'arrival' ? 'manager.punch.ARRIVAL_COORDINATES_ERROR' : 'manager.punch.LEAVE_COORDINATES_ERROR')}</small>
              </div>
            </Alert>
          ) : null}
          {selectedCoordinates ? (
            <div className={isSelectedinGeofence ? 'mt-2' : 'mt-2 text-danger'}>
              <i className="fa-solid fa-map-location-dot mr-1" />
              {`${selectedCoordinates[1].toFixed(5)}, ${selectedCoordinates[0].toFixed(5)}`}
            </div>
          ) : null}
          <div>{punchAddress}</div>
          {expectedLocation ? (
            <>
              <small className="mt-4 text-muted">{t(`manager.punch.EXPECTED_LOCATION`)}:</small>
              <div>
                <i className="fa-solid fa-map-location-dot mr-1" />
                {`${expectedLocation.latitude.toFixed(5)}, ${expectedLocation.longitude.toFixed(5)}`}
              </div>
              <div>{address ? expectedAddress : null}</div>
            </>
          ) : null}
        </div>
        <Map
          features={collection}
          width="100%"
          height="100%"
          selected={selectedCoordinates ? selectedCoordinates : undefined}
          coordinates={initialSelectedCoordinates()}
          expectedLocation={expectedLocation}
          fillColor="gray"
          fillOpacity={0.2}
        />
      </div>
    </ReactModal>
  )
}

export default MapModal
